import { useState } from 'react';
import { paletteValue } from '../palette.js';

/**
 * The icon of a firm's website, always the same size so names line up whether
 * or not a site has one. It is asked of our own api (`/favicons/:domain`, which
 * holds it after the first fetch), never of a third party from the browser; a
 * site with no icon gets its first letter on a neutral tile instead.
 */

/** The host of a website as the api is asked for it, or null where there is none to ask for. */
export function siteOf(website: string | null | undefined): string | null {
  if (website === null || website === undefined) return null;
  const trimmed = website.trim();
  if (trimmed === '') return null;
  try {
    const host = new URL(
      /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`,
    ).hostname
      .toLowerCase()
      .replace(/^www\./, '');
    return host.includes('.') ? host : null;
  } catch {
    return null;
  }
}

function iconUrl(site: string): string {
  return `/favicons/${encodeURIComponent(site)}`;
}

/** Sites the api had no icon for this session, so a re-render does not ask again. */
const withoutIcon = new Set<string>();

/**
 * A site with no icon shows the name's first letter on the neutral chip tint. A
 * site icon is a label the fund carries, and the palette keeps saturated hues
 * for warmth, so the letter does not get a colour of its own.
 */
function monogramOf(name: string): string {
  return (name.trim().match(/[\p{L}\p{N}]/u)?.[0] ?? '?').toUpperCase();
}

export function SiteIcon(props: { site: string | null; name: string; size?: number }) {
  const size = props.size ?? 16;
  const [failedFor, setFailedFor] = useState<string | null>(null);
  const { site } = props;

  if (site === null || failedFor === site || withoutIcon.has(site)) {
    return (
      <span
        className="site-icon site-icon-tile"
        aria-hidden="true"
        style={{ width: size, height: size, fontSize: Math.round(size * 0.58) }}
      >
        {monogramOf(props.name)}
      </span>
    );
  }
  return (
    <img
      className="site-icon"
      src={iconUrl(site)}
      width={size}
      height={size}
      alt=""
      loading="lazy"
      draggable={false}
      onError={() => {
        withoutIcon.add(site);
        setFailedFor(site);
      }}
    />
  );
}

type Loaded =
  { state: 'loading' } | { state: 'ready'; image: HTMLImageElement } | { state: 'none' };
const loaded = new Map<string, Loaded>();

/** `--radius-xs`, the corner the DOM gives the same icon. */
const GRID_ICON_RADIUS = 4;

/**
 * Paints a site's icon into a grid cell, on a canvas that has no <img>.
 * Returns nothing while the icon is on its way; `onReady` is called once it
 * lands so the caller can repaint that cell.
 */
export function paintSiteIcon(
  ctx: CanvasRenderingContext2D,
  options: {
    site: string | null;
    name: string;
    x: number;
    y: number;
    size: number;
    onReady: () => void;
  },
): void {
  const { site, x, y, size } = options;
  const entry = site === null ? ({ state: 'none' } satisfies Loaded) : loaded.get(site);
  if (site !== null && entry === undefined) {
    loaded.set(site, { state: 'loading' });
    const image = new Image();
    image.onload = () => {
      loaded.set(site, { state: 'ready', image });
      options.onReady();
    };
    image.onerror = () => {
      loaded.set(site, { state: 'none' });
      options.onReady();
    };
    image.src = iconUrl(site);
    return;
  }
  if (entry === undefined || entry.state === 'loading') return;

  ctx.save();
  ctx.beginPath();
  ctx.roundRect(x, y, size, size, GRID_ICON_RADIUS);
  ctx.clip();
  if (entry.state === 'ready') {
    ctx.drawImage(entry.image, x, y, size, size);
  } else {
    ctx.fillStyle = paletteValue('--chip-bg');
    ctx.fillRect(x, y, size, size);
    ctx.fillStyle = paletteValue('--chip-text');
    ctx.font = `600 ${Math.round(size * 0.62)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(monogramOf(options.name), x + size / 2, y + size / 2 + 0.5);
  }
  ctx.restore();
}

/** What a cell with an icon in it moves its text over by, and where the icon sits. */
export const GRID_ICON_SIZE = 16;
export const GRID_ICON_INSET = 8;
export const GRID_ICON_TEXT_PADDING = GRID_ICON_INSET + GRID_ICON_SIZE + 8;
